import { create } from 'zustand';

import { normalizeError } from '../../api/core/errors';
import { getStudentEmailDomain } from '../../api/environment';
import { ExamSubmissionRepository } from '../../api/repository/exam-submission';
import { UnitRepository } from '../../api/repository/unit';
import { izteachSession } from '../../api/session';
import type { Id, IztExamQuestion } from '../../api/types';
import { useQltIztContentStore } from '../../stores/qlt';
import { userErrorMessage } from '../../stores/userError';
import { emailPattern } from '../submissions/submissions';
import { examLoadBlock, IZ_SIGNED_OUT, loadClassExamRows, type ExamUnitRef } from './classExams';

export { IZ_SIGNED_OUT };
import { toAttempt, type ExamAttempt } from './examResults';

// per exam; the roster pattern narrows it server side, so only a class retaking one exam hundreds of times hits it
const ATTEMPT_LIMIT = 300;

let loadSeq = 0;

/** The roster and the Kho bài tree query that names the class's IZ courses. */
export interface ExamLoadInput {
  codes: string[];
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
}

/** One exam's questions, for the per-question tallies and each student's sheet. */
export interface ExamQuestionsEntry {
  loading: boolean;
  error: string | null;
  questions: IztExamQuestion[];
}

export interface ExamResultState {
  key: string | null;
  loading: boolean;
  error: string | null;
  attempts: ExamAttempt[];
  courseTitles: Record<string, string>;
  /** exam id to its unit's title, when a token could read it. */
  examTitles: Record<string, string>;
  /** exam id to its test unit, so a row opens its đề on the board. */
  examUnits: Record<string, ExamUnitRef>;
  load: (key: string, input: ExamLoadInput, force?: boolean) => Promise<void>;
  /** By exam id; every open exam tab reads its own. */
  questions: Record<string, ExamQuestionsEntry>;
  loadQuestions: (examId: string, force?: boolean) => Promise<void>;
}

export const useExamResultStore = create<ExamResultState>((set, get) => ({
  key: null,
  loading: false,
  error: null,
  attempts: [],
  courseTitles: {},
  examTitles: {},
  examUnits: {},

  async load(key, input, force = false) {
    const state = get();
    if (!force && state.key === key && !state.error && (state.loading || state.attempts.length > 0)) return;
    loadSeq += 1;
    const seq = loadSeq;
    const domain = getStudentEmailDomain();
    const pattern = domain ? emailPattern(input.codes, domain) : '';
    const block = examLoadBlock(domain, pattern, Boolean(izteachSession.accessToken));
    if (block) { set({ key, loading: false, error: block.error, attempts: [] }); return; }
    set({ key, loading: true, error: null, attempts: state.key === key ? state.attempts : [] });
    try {
      const tree = await useQltIztContentStore.getState().fetchTree({ class: input.classId, grade: input.gradeId, subject: input.subjectId });
      const found = await loadClassExamRows({
        unitsOf: UnitRepository.titlesForCourse,
        attemptsOf: (examId) => ExamSubmissionRepository.listByExam(examId, pattern, ATTEMPT_LIMIT),
      }, tree, pattern);
      if (seq !== loadSeq) return;
      const attempts: ExamAttempt[] = [];
      found.rows.forEach((row) => {
        const attempt = toAttempt(row, domain);
        if (attempt) attempts.push(attempt);
      });
      set({ loading: false, attempts, examTitles: { ...get().examTitles, ...found.examTitles }, examUnits: { ...get().examUnits, ...found.examUnits }, courseTitles: { ...get().courseTitles, ...found.courseTitles } });
    } catch (error) {
      if (seq !== loadSeq) return;
      const kind = normalizeError(error).kind;
      set({ loading: false, error: kind === 'unauthorized' || kind === 'forbidden' ? IZ_SIGNED_OUT : userErrorMessage(error) });
    }
  },

  questions: {},

  async loadQuestions(examId, force = false) {
    const current = get().questions[examId];
    if (current && !force && (current.loading || !current.error)) return;
    const put = (entry: ExamQuestionsEntry) => set({ questions: { ...get().questions, [examId]: entry } });
    put({ loading: true, error: null, questions: current ? current.questions : [] });
    try {
      put({ loading: false, error: null, questions: await ExamSubmissionRepository.questionsOf(examId) });
    } catch (error) {
      put({ loading: false, error: userErrorMessage(error), questions: [] });
    }
  },
}));
